import React, { useEffect, useState } from "react";
import "../viewRequestDetails/ViewRequestDetails.css";
import "../../../patientFullView/patientFullView.css";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Widgets from "../../../../widgets";
import strings from "../../../../utils/strings";
import PatientPersonalInfo from "../../../patientFullView/patientPersonalInfo";
import { getNurseSessionDetails, getNurseRequestDetails } from "../../../../modules/NursignCare/ManageRequest/manageRequestThunk";

const formatDateTime = (dateTime) => {
  if (!dateTime) return "-";
  const d = new Date(dateTime);
  if (isNaN(d)) return String(dateTime);
  const date = d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  const time = d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true });
  return `${date} at ${time}`;
};

// cancelledBy may come back as a populated user object ({_id, email, firstName, lastName}) rather than a string
const formatPersonName = (val) => {
  if (!val) return "-";
  if (typeof val === "string") return val;
  const first = typeof val.firstName === "string" ? val.firstName : val.firstName?.en || val.firstName?.vi || "";
  const last = typeof val.lastName === "string" ? val.lastName : val.lastName?.en || val.lastName?.vi || "";
  return `${first} ${last}`.trim() || val.name || val.email || "-";
};

const DeletedSessionDetails = () => {
  const { t } = useTranslation();
  const nav = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const returnTo = searchParams.get("returnTo");

  const { sessionDetails, requestDetails } = useSelector((state) => state.manageRequest);
  const [sessionLoading, setSessionLoading] = useState(true);

  const breadcrumbs = ["nursingCare", "manageRequests", "sessionDetails"];
  const paths = [strings.navigation.manageNurseReq, `${strings.navigation.manageNurseReq}?activeList=deletedSessions`, strings.navigation.viewDeletedSessionDetails];

  useEffect(() => {
    if (!id) return;
    setSessionLoading(true);
    dispatch(getNurseSessionDetails({ id })).finally(() => setSessionLoading(false));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const requestId = sessionDetails?.nursingRequest?._id;
  useEffect(() => {
    if (requestId) dispatch(getNurseRequestDetails({ id: requestId }));
  }, [requestId]); // eslint-disable-line react-hooks/exhaustive-deps

  const session = sessionDetails;
  const request = requestId ? requestDetails : null;

  const handleBack = () => {
    if (returnTo) {
      nav(decodeURIComponent(returnTo));
      return;
    }
    nav(`${strings.navigation.manageNurseReq}?activeList=deletedSessions`);
  };

  const patientData = {
    firstName: request?.patient?.fullName || "",
    lastName: "",
    hnNumber: request?.patient?.hnNumber,
    gender: request?.patient?.gender,
    dateOfBirth: request?.patient?.dateOfBirth,
    phoneNumber: request?.patient?.phoneNumber,
    countryCode: request?.patient?.countryCode,
    email: request?.patient?.email,
    insuranceCategory: request?.patient?.insuranceCategory,
    profilePhoto: request?.patient?.profilePhoto,
  };

  return (
    <div className="pdl-container service-details-wrapper">
      <Widgets.BreadCrumb breadcrumbs={breadcrumbs} paths={paths} />

      <div className="department-heading pdl">
        <div>{t("sessionDetails") || "Session Details"}</div>
        <div className="pdl-buttons">
          <div className="pdl-btn verified pav-status" style={{ backgroundColor: "#fee2e2", color: "#ef4444", border: "none" }}>
            {session?.status ? t(session.status) : "-"}
          </div>
          <span className="req-id-text">{t("request_id")}: {session?.nursingRequest?.nursingRequestID || "-"}</span>
        </div>
      </div>

      {sessionLoading ? (
        <div className="pdl-info">{t("loading")}…</div>
      ) : (
        <>
          <div className="pdl-info">
            <PatientPersonalInfo data={patientData} />
          </div>

          <div className="pdl-info">
            <div className="notes-container no-bg-border">
              <div className="green-bar"></div>
              <div className="notes-content">
                <h4 className="notes-title" style={{ color: "#2d3436" }}>
                  {t("sessionDateTime")}
                </h4>
                <p className="notes-text">{formatDateTime(session?.startTime)}</p>
              </div>
            </div>
          </div>

          {/* Cancellation Details */}
          <div className="pdl-info">
            <p className="pdl-title">{t("cancellationDetails")}</p>
            <div className="pdl-body flex-row">
              <div className="pref-box">
                <span className="pref-lbl">{t("cancellationReason")}</span>
                <span className="pref-val">{session?.cancellationReason || "-"}</span>
              </div>
              <div className="pref-box">
                <span className="pref-lbl">{t("cancelledBy")}</span>
                <span className="pref-val">{formatPersonName(session?.cancelledBy || session?.cmdCenterAdmin)}</span>
              </div>
              <div className="pref-box">
                <span className="pref-lbl">{t("cancelledAt")}</span>
                <span className="pref-val">{formatDateTime(session?.cancelledAt || session?.updatedAt)}</span>
              </div>
            </div>
          </div>
        </>
      )}

      <div className="form-section-footer">
        <button className="form-section-cancel" onClick={handleBack}>
          {t("back")}
        </button>
      </div>
    </div>
  );
};

export default DeletedSessionDetails;
